import React from 'react';
import { Building2, TrendingUp, Globe, MapIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

/**
 * POC Statistics Cards Component
 * Displays key metrics about POCs and Amplify POCs
 */
export function POCStatisticsCards({ statistics, isLoading }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <CardContent className="p-6">
              <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!statistics) {
    return null;
  }

  const stats = [
    {
      title: 'Total POCs',
      value: statistics.total_pocs || 0,
      icon: Building2,
      color: 'blue',
      bgColor: 'bg-blue-50 dark:bg-blue-900/30',
      textColor: 'text-blue-600 dark:text-blue-400',
      borderColor: 'border-blue-200 dark:border-blue-800'
    },
    {
      title: 'Amplify POCs',
      value: statistics.total_amplify_pocs || 0,
      icon: TrendingUp,
      color: 'green',
      bgColor: 'bg-green-50 dark:bg-green-900/30',
      textColor: 'text-green-600 dark:text-green-400',
      borderColor: 'border-green-200 dark:border-green-800'
    },
    {
      title: 'Total Countries',
      value: Object.keys(statistics.pocs_by_country || {}).length,
      icon: Globe,
      color: 'purple',
      bgColor: 'bg-purple-50 dark:bg-purple-900/30',
      textColor: 'text-purple-600 dark:text-purple-400',
      borderColor: 'border-purple-200 dark:border-purple-800'
    },
    {
      title: 'Total Regions',
      value: Object.keys(statistics.pocs_by_region || {}).length,
      icon: MapIcon,
      color: 'orange',
      bgColor: 'bg-orange-50 dark:bg-orange-900/30',
      textColor: 'text-orange-600 dark:text-orange-400',
      borderColor: 'border-orange-200 dark:border-orange-800'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <Card key={index} className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {stat.value.toLocaleString()}
                  </p>
                  {stat.title === 'Total POCs' && statistics.recently_added > 0 && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      +{statistics.recently_added} recently added
                    </p>
                  )}
                </div>
                <div className={`p-3 rounded-lg border ${stat.bgColor} ${stat.borderColor}`}>
                  <Icon className={`h-6 w-6 ${stat.textColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Regional Breakdown - Takes 2 columns on larger screens */}
      {statistics.pocs_by_region && Object.keys(statistics.pocs_by_region).length > 0 && (
        <Card className="lg:col-span-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <MapIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              POCs by Region
            </h4>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(statistics.pocs_by_region)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 6)
                .map(([region, count]) => (
                  <div key={region} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{region}</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Country Breakdown - Takes 2 columns on larger screens */}
      {statistics.pocs_by_country && Object.keys(statistics.pocs_by_country).length > 0 && (
        <Card className="lg:col-span-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Globe className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              POCs by Country
            </h4>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(statistics.pocs_by_country)
                .sort(([, a], [, b]) => b - a)
                .map(([country, count]) => (
                  <div key={country} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{country}</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default POCStatisticsCards;
